import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import MetaTags from '../components/MetaTags';
import MembershipForm from '../components/MembershipForm';
import api from '../api/axios';
import './new-pages.css';
import './membership-partners.css';

// MembershipLookup removed — now lives at /membership-lookup with wallet support

const BENEFITS = [
  { icon: 'fa-ticket-alt', title: 'Event Access', desc: 'Discounted or free entry to all MNCW cultural events and celebrations throughout the year.' },
  { icon: 'fa-vote-yea', title: 'Voting Rights', desc: 'Vote at the AGM and have a say in how MNCW is run. Stand for election to the committee.' },
  { icon: 'fa-hand-holding-heart', title: 'Community Support', desc: 'Access to community welfare programs, referral services, and mutual support networks.' },
  { icon: 'fa-graduation-cap', title: 'Cultural Education', desc: 'Access to MNCW cultural programs and Nepali language activities for the whole family.' },
  { icon: 'fa-bell', title: 'News & Updates', desc: 'Stay informed with the latest MNCW news, event announcements, and community updates.' },
  { icon: 'fa-users', title: 'Network & Connect', desc: 'Build friendships and connections with Nepalese families across Western Melbourne.' },
];

const PLANS = [
  {
    key: 'individual',
    label: 'Individual',
    price: '$25',
    period: '2-year validity · Single person',
    type: 'membership_single',
    features: ['Access to all cultural events', 'Voting rights at AGM', 'Community newsletter', 'Support for welfare programs', 'Sporting club membership (Separate club fee may apply)'],
  },
  {
    key: 'family',
    label: 'Family',
    price: '$50',
    period: '2-year validity · Couple & children',
    type: 'membership_family',
    features: ['Access to all cultural events', 'Voting rights at AGM', 'Community newsletter', 'Full family coverage', 'Support for welfare programs', 'Sporting club membership (Separate club fee may apply)'],
  },
];

export default function MembershipPage() {
  const [site, setSite] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const { hash } = useLocation();

  useEffect(() => {
    Promise.all([
      api.get('/content/site'),
      api.get('/partners').catch(() => ({ data: [] })),
    ]).then(([siteRes, partnersRes]) => {
      setSite(siteRes.data);
      setPartners(partnersRes.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!loading && hash) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [loading, hash]);

  if (loading) return <div className="page-loading"><div className="spinner-ring"></div></div>;
  if (!site) return null;

  return (
    <div className="App">
      <MetaTags
        title="Membership | Melbourne Nepalese Community West"
        description="Join MNCW — Individual membership $25/year or Family membership $50 for 2 years. Access community events, voting rights, and support programs."
        url="https://mncw.org.au/membership"
        image="https://mncw.org.au/assets/img/logo.jpg"
        type="website"
      />
      <Header data={site} />

      {/* Benefits */}
      <div className="community-page-container bg-white">
        <div className="container">
          <div className="page-header">
            <span className="page-subtitle">Why Join?</span>
            <h1>Member Benefits</h1>
          </div>
          <div className="benefits-grid">
            {BENEFITS.map(b => (
              <div key={b.title} className="benefit-card">
                <div className="benefit-icon"><i className={`fas ${b.icon}`}></i></div>
                <h4>{b.title}</h4>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Business Partners */}
      {partners.length > 0 && (
        <div className="page-section bg-light mp-partners-section">
          <div className="container">
            <div className="section-divider">
              <span className="section-tag">Member Exclusive</span>
              <h2>Business Partner Discounts</h2>
              <p className="page-intro" style={{ marginTop: 12 }}>
                As an MNCW member you get exclusive discounts at our partner businesses across the community.
              </p>
            </div>
            <div className="mp-partners-grid">
              {partners.map(p => (
                <div key={p.id} className="mp-partner-card">
                  {p.logo ? (
                    <img src={p.logo} alt={p.name} className="mp-partner-logo" />
                  ) : (
                    <div className="mp-partner-logo-placeholder">
                      <i className="fas fa-store"></i>
                    </div>
                  )}
                  <div className="mp-partner-body">
                    {p.category && <span className="mp-partner-cat">{p.category}</span>}
                    <h4 className="mp-partner-name">{p.name}</h4>
                    {p.description && <p className="mp-partner-desc">{p.description}</p>}
                    {p.discount && (
                      <div className="mp-partner-discount">
                        <i className="fas fa-tag"></i> {p.discount}
                      </div>
                    )}
                    {(p.suburb || p.state) && (
                      <div className="mp-partner-location">
                        <i className="fas fa-map-marker-alt"></i> {[p.suburb, p.state].filter(Boolean).join(', ')}
                      </div>
                    )}
                    {p.website && (
                      <a href={p.website} target="_blank" rel="noopener noreferrer" className="mp-partner-link">
                        Visit Website <i className="fas fa-external-link-alt"></i>
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Plans */}
      <div id="pricing" className="page-section bg-white">
        <div className="container">
          <div className="section-divider">
            <span className="section-tag">Membership Plans</span>
            <h2>Choose Your Plan</h2>
            <p className="page-intro" style={{ marginTop: 12 }}>Select the plan that best fits you and your family.</p>
          </div>
          <div className="membership-plans-grid">
            {PLANS.map(plan => (
              <div key={plan.key} className="plan-card">
                <span style={{ fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, color: 'var(--primary-red)' }}>{plan.label} Plan</span>
                <div className="plan-price">{plan.price}</div>
                <p className="plan-period">{plan.period}</p>
                <ul className="plan-features">
                  {plan.features.map(f => (
                    <li key={f}><i className="fas fa-check"></i>{f}</li>
                  ))}
                </ul>
                <a href="#membership-form" className="btn-mncw" style={{ justifyContent: 'center', width: '100%' }}>
                  Register Now <i className="fas fa-arrow-right"></i>
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Check My Membership CTA */}
      <div id="check-membership" className="page-section bg-white">
        <div className="container">
          <div className="section-divider">
            <span className="section-tag">Already a Member?</span>
            <h2>Check My Membership</h2>
            <p className="page-intro" style={{ marginTop: 12 }}>
              View your membership status, number, expiry date — and save your card to Google or Apple Wallet.
            </p>
            <a href="/membership-lookup" className="btn-mncw" style={{ display: 'inline-flex', marginTop: 8 }}>
              <i className="fas fa-id-card" style={{ marginRight: 8 }}></i>Check Now
            </a>
          </div>
        </div>
      </div>

      {/* Registration Form */}
      <div className="membership-form-section" id="membership-form">
        <div className="container">
          <div className="form-card">
            <p className="form-subtitle">Membership Registration</p>
            <h2>Join MNCW Today</h2>
            <MembershipForm />
          </div>
        </div>
      </div>

      <Footer data={site} />
    </div>
  );
}
